import React, { useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import {
  EMPTY_FIELD_LIST,
  FIELD_CHANGES_PANEL_TITLE,
  NO_FIELD_CHANGES_NOTE,
} from '../../EntityAuditHistory.constants';
import {
  AuditChangePanelsPropType,
  AuditFieldListPropType,
  AuditRenderersPropType,
} from '../../EntityAuditHistory.propTypes';
import { changesPanelsStyles } from '../../EntityAuditHistory.styles';
import { EntityAuditHistoryTestIds } from '../../EntityAuditHistory.testIds';
import {
  getFieldChangesSubtitle,
  getSummaryRenderer,
  splitPanelChanges,
  withChangeRowKeys,
} from '../../EntityAuditHistory.utils';
import AuditChangesPanel from '../AuditChangesPanel';
import AuditChangesTable from '../AuditChangesTable';
import AuditPanelSubtitle from '../AuditPanelSubtitle';
import AuditSummaryValue from '../AuditSummaryValue';

/**
 * The Changes tab. Without `changePanels`, or when none takes a change of this row, it is the field
 * diff alone; otherwise the fields sit one per row under "Field changes" and each panel renders its
 * summarised changes at full width, each panel's count next to its title.
 */
const AuditDetailChanges = ({
  changes,
  keyPrefix,
  operation = null,
  renderers = null,
  changePanels = null,
  automaticFields = EMPTY_FIELD_LIST,
  row = null,
  detail = null,
}) => {
  const { fieldChanges, panels } = useMemo(
    () => splitPanelChanges(changes, changePanels),
    [changes, changePanels],
  );

  if (changes.length === 0) {
    return (
      <Typography
        color="text.secondary"
        data-testid={EntityAuditHistoryTestIds.NO_CHANGES}
        variant="regular14"
      >
        {NO_FIELD_CHANGES_NOTE}
      </Typography>
    );
  }

  if (panels.length === 0) {
    return (
      <AuditChangesTable
        automaticFields={automaticFields}
        changes={fieldChanges}
        detail={detail}
        keyPrefix={keyPrefix}
        operation={operation}
        renderers={renderers}
        row={row}
      />
    );
  }

  return (
    <Box sx={changesPanelsStyles}>
      {fieldChanges.length > 0 && (
        <AuditChangesPanel
          subtitle={
            <AuditPanelSubtitle
              changes={fieldChanges}
              detail={detail}
              text={getFieldChangesSubtitle(fieldChanges, automaticFields)}
            />
          }
          title={FIELD_CHANGES_PANEL_TITLE}
        >
          <AuditChangesTable
            isInline
            automaticFields={automaticFields}
            changes={fieldChanges}
            detail={detail}
            keyPrefix={keyPrefix}
            operation={operation}
            renderers={renderers}
            row={row}
          />
        </AuditChangesPanel>
      )}
      {panels.map(panel => (
        <AuditChangesPanel
          key={panel.id}
          subtitle={
            panel.subtitle ? (
              <AuditPanelSubtitle
                changes={panel.changes}
                detail={detail}
                renderer={panel.subtitle}
              />
            ) : null
          }
          title={panel.title}
        >
          {withChangeRowKeys(`${keyPrefix}:${panel.id}`, panel.changes).map(change => (
            <AuditSummaryValue
              change={change}
              detail={detail}
              key={change.key}
              renderer={getSummaryRenderer(renderers, change.summary)}
              row={row}
            />
          ))}
        </AuditChangesPanel>
      ))}
    </Box>
  );
};

AuditDetailChanges.propTypes = {
  changes: PropTypes.arrayOf(PropTypes.shape({ field: PropTypes.string.isRequired })).isRequired,
  keyPrefix: PropTypes.string.isRequired,
  operation: PropTypes.string,
  renderers: AuditRenderersPropType,
  changePanels: AuditChangePanelsPropType,
  automaticFields: AuditFieldListPropType,
  row: PropTypes.object,
  detail: PropTypes.object,
};

export default AuditDetailChanges;
